import camelcaseKeys from "camelcase-keys";

import { supabase } from "@/lib/supabase";

import { API_ENDPOINTS } from "../constants/api";
import { PROMPTS_TABLE_NAME } from "../store/const";
import { aggregateBrandEveryDaySummaryByBrand } from "../store/scores/utils/brandEveryDayFromRows";
import { ScoreDailyAverageResponse } from "../store/scores/response.types";
import type { Brand7dSummary } from "../store/scores/types";
import { ManagedAccountBrand } from "../store/agency/types";
import { apiGet } from "../utils/apiClient";
import { scoresApi } from "./scores.api";
import {
  AccountContentsResponse,
  AccountScoreListItem,
} from "./agencyBrandStats.types";

const AGENCY_CLIENT_API_OPTIONS = {
  auth: "tokenApi" as const,
  skipActingAs: true,
};

function accountContentsUrl(accountId: string, agencyUserId: string): string {
  const url = new URL(API_ENDPOINTS.agencyAccountContents(accountId));
  url.searchParams.set("user_id", agencyUserId);
  return url.toString();
}

function normalizeContents(data: unknown): AccountContentsResponse {
  return camelcaseKeys(data as Record<string, unknown>, { deep: true }) as AccountContentsResponse;
}

/** Latest score row per brand from account contents. */
function indexLatestScoresByBrand(scores: AccountScoreListItem[]): Map<number, AccountScoreListItem> {
  const byBrand = new Map<number, AccountScoreListItem>();

  for (const score of scores) {
    const brandId = score.brandId;
    if (!Number.isFinite(brandId)) continue;

    const existing = byBrand.get(brandId);
    if (!existing) {
      byBrand.set(brandId, score);
      continue;
    }

    const existingTs = existing.updatedAt ? Date.parse(existing.updatedAt) : 0;
    const scoreTs = score.updatedAt ? Date.parse(score.updatedAt) : 0;
    if (scoreTs >= existingTs) {
      byBrand.set(brandId, score);
    }
  }

  return byBrand;
}

function countPromptsByBrand(
  prompts: AccountContentsResponse["prompts"],
): Map<number, number> {
  const counts = new Map<number, number>();

  for (const prompt of prompts) {
    const brandId = prompt.brandId;
    if (!Number.isFinite(brandId)) continue;
    counts.set(brandId!, (counts.get(brandId!) ?? 0) + 1);
  }

  return counts;
}

async function fetchPromptCountsByBrand(clientOwnerId: string): Promise<Map<number, number>> {
  const { data, error } = await supabase
    .from(PROMPTS_TABLE_NAME)
    .select("brand_id")
    .eq("user_id", clientOwnerId)
    .is("deleted_at", null);

  if (error || !data) {
    return new Map();
  }

  const counts = new Map<number, number>();
  for (const row of data) {
    const brandId = row.brand_id as number | null;
    if (!Number.isFinite(brandId)) continue;
    counts.set(brandId!, (counts.get(brandId!) ?? 0) + 1);
  }

  return counts;
}

async function fetchBrandVisibilityByBrand(
  clientOwnerId: string,
): Promise<Map<number, Brand7dSummary>> {
  try {
    const rows = (await scoresApi.fetchBrandScoreDays({
      userID: clientOwnerId,
      days: 14,
    })) as ScoreDailyAverageResponse[];
    const summary = aggregateBrandEveryDaySummaryByBrand(rows);

    return new Map(
      Object.entries(summary).map(([brandId, item]) => [Number(brandId), item]),
    );
  } catch {
    return new Map();
  }
}

export function mergeBrandStats(
  brands: ManagedAccountBrand[],
  promptCounts: Map<number, number>,
  visibilityByBrand: Map<number, Brand7dSummary>,
): ManagedAccountBrand[] {
  return brands.map((brand) => {
    const visibilityStats = visibilityByBrand.get(brand.id);

    return {
      ...brand,
      promptCount: brand.promptCount > 0 ? brand.promptCount : (promptCounts.get(brand.id) ?? 0),
      currentScore: visibilityStats?.avg_visibility ?? brand.currentScore,
      scoreTrend: visibilityStats?.week_difference_visibility ?? brand.scoreTrend,
    };
  });
}

/** Maps account contents into per-brand dashboard stats. */
export function mapAccountContentsToBrandStats(
  contents: AccountContentsResponse,
): ManagedAccountBrand[] {
  const scoresByBrand = indexLatestScoresByBrand(contents.scores ?? []);
  const promptsByBrand = countPromptsByBrand(contents.prompts ?? []);

  return (contents.brands ?? []).map((brand) => {
    const score = scoresByBrand.get(brand.id);

    return {
      id: brand.id,
      name: brand.name,
      promptCount: promptsByBrand.get(brand.id) ?? 0,
      currentScore: score?.value ?? null,
      scoreTrend: null,
      updatedAt: score?.updatedAt ?? "",
    };
  });
}

/**
 * Loads brands and per-brand stats for a managed client account.
 * Agency must be authorized to read the client's account contents.
 */
export async function fetchClientBrandStats(
  clientAccountId: string,
  clientOwnerId: string,
  agencyUserId: string,
): Promise<ManagedAccountBrand[]> {
  const [contentsResult, promptCounts, visibilityByBrand] = await Promise.all([
    apiGet<AccountContentsResponse>(
      accountContentsUrl(clientAccountId, agencyUserId),
      {
        ...AGENCY_CLIENT_API_OPTIONS,
        headers: {
          "X-Acting-As": clientAccountId,
        },
      },
    ),
    fetchPromptCountsByBrand(clientOwnerId),
    fetchBrandVisibilityByBrand(clientOwnerId),
  ]);

  const brands = mapAccountContentsToBrandStats(normalizeContents(contentsResult));
  return mergeBrandStats(brands, promptCounts, visibilityByBrand);
}
